import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { useRef, useMemo, Suspense } from "react";
import * as THREE from "three";

function Coin() {
  const group = useRef();
  const logo = useLoader(THREE.TextureLoader, "/trst-logo.png");
  logo.colorSpace = THREE.SRGBColorSpace;
  logo.anisotropy = 8;

  useFrame((state, delta) => {
    if (group.current) {
      group.current.rotation.y += delta * 0.5;
      group.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.4) * 0.12;
    }
  });
  return (
    <group ref={group} rotation={[0.35, 0, 0.1]}>
      {/* Coin edge/body */}
      <mesh castShadow>
        <cylinderGeometry args={[1.55, 1.55, 0.26, 72]} />
        <meshStandardMaterial color="#e9b949" metalness={1} roughness={0.28} emissive="#4a3406" emissiveIntensity={0.35} />
      </mesh>
      {/* Top face — logo */}
      <mesh position={[0, 0.135, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[1.5, 72]} />
        <meshStandardMaterial map={logo} metalness={0.5} roughness={0.45} emissive="#2a1e00" emissiveIntensity={0.25} transparent />
      </mesh>
      {/* Bottom face — logo */}
      <mesh position={[0, -0.135, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <circleGeometry args={[1.5, 72]} />
        <meshStandardMaterial map={logo} metalness={0.5} roughness={0.45} emissive="#2a1e00" emissiveIntensity={0.25} transparent />
      </mesh>
      {/* Glow rim */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.7, 0.03, 16, 100]} />
        <meshStandardMaterial color="#f4c95d" emissive="#f4c95d" emissiveIntensity={2.2} toneMapped={false} />
      </mesh>
    </group>
  );
}

function Particles({ count = 900 }) {
  const points = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 4 + Math.random() * 7;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = (Math.random() - 0.5) * 10;
      arr[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
    }
    return arr;
  }, [count]);

  useFrame((state, delta) => {
    if (points.current) points.current.rotation.y += delta * 0.03;
  });

  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.045} color="#f4c95d" transparent opacity={0.65} sizeAttenuation depthWrite={false} />
    </points>
  );
}

function OrbitCoin({ radius, speed, offset, scale, map, tint = "#e9b949" }) {
  const ref = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime * speed + offset;
    if (ref.current) {
      ref.current.position.set(Math.cos(t) * radius, Math.sin(t * 0.8) * 0.8, Math.sin(t) * radius);
      ref.current.rotation.y += 0.03;
      ref.current.rotation.x += 0.02;
    }
  });
  return (
    <group ref={ref} scale={scale}>
      {/* edge */}
      <mesh>
        <cylinderGeometry args={[0.5, 0.5, 0.12, 44]} />
        <meshStandardMaterial color={tint} metalness={1} roughness={0.28} emissive="#2a1e00" emissiveIntensity={0.4} />
      </mesh>
      {/* faces with coin logo */}
      <mesh position={[0, 0.062, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.47, 44]} />
        <meshStandardMaterial map={map} metalness={0.4} roughness={0.5} transparent />
      </mesh>
      <mesh position={[0, -0.062, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.47, 44]} />
        <meshStandardMaterial map={map} metalness={0.4} roughness={0.5} transparent />
      </mesh>
    </group>
  );
}

export default function HeroCanvas() {
  const [trt, btc, eth, usdt] = useLoader(THREE.TextureLoader, [
    "/trst-logo.png",
    "/btc.png",
    "/eth.png",
    "/usdt.png",
  ]);
  [trt, btc, eth, usdt].forEach((t) => {
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
  });
  return (
    <Canvas
      dpr={[1, 1.8]}
      camera={{ position: [0, 0.6, 6.2], fov: 42 }}
      gl={{ antialias: true, alpha: true, powerPreference: "high-performance", failIfMajorPerformanceCaveat: false }}
      style={{ background: "transparent" }}
      onCreated={({ gl }) => {
        const canvas = gl.domElement;
        canvas.addEventListener(
          "webglcontextlost",
          (e) => e.preventDefault(),
          false
        );
      }}
    >
      <Suspense fallback={null}>
        <ambientLight intensity={0.55} />
        <directionalLight position={[5, 6, 5]} intensity={2.4} color="#fff6df" />
        <pointLight position={[-6, -2, -4]} intensity={2.2} color="#4c7dff" />
        <pointLight position={[4, -3, 3]} intensity={1.4} color="#f4c95d" />
        <group rotation={[0, 0, 0]}>
          <Coin />
          <OrbitCoin radius={3.1} speed={0.5} offset={0} scale={0.62} map={btc} tint="#f7931a" />
          <OrbitCoin radius={3.6} speed={-0.35} offset={2} scale={0.5} map={eth} tint="#6b7bb8" />
          <OrbitCoin radius={2.8} speed={0.42} offset={4} scale={0.42} map={usdt} tint="#26a17b" />
        </group>
        <Particles />
      </Suspense>
    </Canvas>
  );
}
